import { useRef, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { kebabCase } from "lodash";

import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { BoxedContent } from "@/components/common";
import {
  DynamicFormContainer,
} from "@/components/dynamic";
import {
  getDynamicObjectRecordById,
} from "@/api/dynamic/dynamicObjectNameApi";
import dynamicObjectMap from "@/utils/maps/dynamicObjectMap";
import { formatDecimalValues } from "@/utils/helpers";
import { useTaskRedirect } from "@/hooks";

const formatMaterialRequest = (data) => {
  const initialState = {
    stockItem: data?.itemDetail?.map((item) => ({
      ...item,
      price: item.sellingRate || 10,
      amount: item.sellingRate ?
        item.sellingRate * item.quantity :
        10 * item.quantity,
      billableExpense: true,
      item: {
        ...item.item,
        key: item.item.id,
        value: item.item.id,
        label: item.item.itemName,
      },
    })),
    source: data?.itemDetail?.map((item) => ({
      warehouse: null,
      quantityAvailable: 0,
      quantityRequired: item.quantity,
      item: {
        ...item.item,
        key: item.item.id,
        value: item.item.id,
        label: item.item.itemName,
      },
    })),
    requiredFor: {
      label: data?.requiredFor,
      value: data?.requiredFor,
    },
  };

  if (initialState.stockItem) {
    const totalAmount = initialState.stockItem.reduce(
      (total, currentValue) => total + Number(currentValue.amount),
      0
    );

    initialState.totalAmount = totalAmount;
  }

  return initialState;
};

function StockTransferForm() {
  const ref = useRef(null);
  const [initialState, setInitialState] = useState({});
  const { redirect } = useTaskRedirect();
  const [searchParams] = useSearchParams();
  const materialRequest = searchParams.get("materialRequest");

  const { data: materialRequestData } = useQuery(
    [
      kebabCase(dynamicObjectMap.get("MaterialRequestObjectName")),
      materialRequest,
    ],
    () =>
      getDynamicObjectRecordById(
        kebabCase(dynamicObjectMap.get("MaterialRequestObjectName")),
        materialRequest
      ),
    {
      enabled: Boolean(materialRequest),
    }
  );

  useEffect(() => {
    if (materialRequestData) {
      const formattedMaterialRequest = formatMaterialRequest(materialRequestData);
      setInitialState((prevState) => ({
        ...prevState,
        ...formattedMaterialRequest
      }));
    }
  }, [materialRequestData]);

  const setTotal = (value) => {
    const totalApproved = value.reduce((sum, stockItem) => {
      if (stockItem.billableExpense) {
        return sum + Number(stockItem.amount || 0);
      }
      return sum;
    }, 0);

    ref.current.setFormState({
      totalAmount: totalApproved,
    });
  };

  const onStateChange = (key, value) => {
    if (key === "stockItem") {
      setTotal(value);
    }
  };

  const onChildStateChange = ({
    index, key, value, parentField, parentFieldType
  }) => {
    const formState = ref.current.getState();
    const stateKey = `${parentField}${parentFieldType}`;
    let parentFieldState = formState[stateKey] ?? {};

    if (index > -1) {
      parentFieldState = formState[parentField][index];
    }

    if (parentField === "stockItem") {
      switch (key) {
        case "quantity":
        case "price": {
          parentFieldState[key] = value;
          const { price, quantity } = parentFieldState;
          const amount = Number(price) * Number(quantity);
          parentFieldState.amount = formatDecimalValues(amount);
          break;
        }
        default:
          break;
      }
    }
  };

  const onBeforeSave = () => {
    const formState = ref.current.getState();
    const { source } = formState;

    if (source.length) {
      const { quantityAvailable, quantityRequired } = source[0];

      if (quantityAvailable < quantityRequired) {
        toast.error("Quantity Available must be equal or more than quantity required");
        return false;
      }
    }

    return true;
  };

  return (
    <BoxedContent>
      <DynamicFormContainer
        ref={ref}
        initialData={initialState}
        objectName={dynamicObjectMap.get("StockTransferObjectName")}
        showHeader
        showLinkedViews
        onStateChange={onStateChange}
        onChildStateChange={onChildStateChange}
        onBeforeSave={onBeforeSave}
        onSuccess={(newRecordId) =>
          redirect(-1, {
            recordId: newRecordId,
            success: true,
          })}
        navigate={false}
      />
    </BoxedContent>
  );
}

export default StockTransferForm;
